import { ArrowRightLeftIcon } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button, TooltipButton } from "~/components/ui/button"
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { Field, FieldLabel } from "~/components/ui/field"
import { IssueSetting } from "~/components/issueDetailsPage/IssueSetting"
import { TeamPermissions } from "~/lib/permissions"
import { IssueStatus } from "~/lib/enums"
import { api } from "~/trpc/react"

export function IssueTransferFields({
	teams,
	value,
	onChange,
	disabled
}: {
	teams: { id: string; name: string; slug: string }[]
	value: string | null
	onChange: (value: string | null) => void
	disabled?: boolean
}) {
	return (
		<>
			<Field>
				<FieldLabel>Target team</FieldLabel>
				<IssueSetting<string | null>
					requireTeam={false}
					value={value}
					onChange={onChange}
					ariaLabel="Target team"
					placeholder="Choose a team"
					disabled={disabled}
					withSearch
					showArrow
					showBorder
					options={teams.map((team) => ({
						value: team.id,
						searchText: `${team.name} ${team.slug}`,
						display: <span>{team.name}</span>
					}))}
				/>
			</Field>
			<p className="text-sm text-text-sub">
				The issue gets a new key. Content, comments and history stay with it.
				Project, labels, blockers and source GitHub/Discord links are removed;
				remote issues and threads are unchanged. The assignee stays only if they
				belong to the target team. Unsupported difficulty is cleared. Links
				using the old issue key will stop working.
			</p>
		</>
	)
}

export function IssueTransferDialog({
	issueId,
	teamId,
	organizationId,
	updatedAt,
	status,
	disabled
}: {
	issueId: string
	teamId: string
	organizationId: string
	updatedAt: Date
	status: IssueStatus
	disabled?: boolean
}) {
	const [open, setOpen] = useState(false)
	const [targetTeamId, setTargetTeamId] = useState<string | null>(null)
	const navigate = useNavigate()
	const utils = api.useUtils()
	const teams = api.user.getTeams.useQuery(
		{
			organizationId,
			teamPermissionFilter: [
				status === IssueStatus.Queue
					? TeamPermissions.CREATE_QUEUE_ISSUES
					: TeamPermissions.CREATE_ISSUES
			]
		},
		{ enabled: open }
	)
	const targets =
		teams.data?.filter(
			(team) =>
				team.id !== teamId &&
				team.enableIssues &&
				(status !== IssueStatus.Queue || team.enableQueue)
		) ?? []
	const transfer = api.issues.transfer.useMutation({
		onSuccess: async (result) => {
			setOpen(false)
			navigate(result.issueUrl)
			await Promise.all([
				utils.issues.invalidate(),
				utils.user.getTeams.invalidate(),
				utils.favorites.list.invalidate(),
				utils.projects.invalidate(),
				utils.github.invalidate()
			])
			toast.success("Issue transferred")
		},
		onError: (error) => toast.error(error.message)
	})
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!transfer.isPending) {
					setOpen(next)
					if (next) setTargetTeamId(null)
				}
			}}
		>
			<DialogTrigger asChild>
				<TooltipButton
					variant="outline"
					size="icon-md"
					tooltip="Transfer issue"
					disabled={disabled}
				>
					<ArrowRightLeftIcon />
				</TooltipButton>
			</DialogTrigger>
			<DialogContent asChild size="md" layout="scroll">
				<form
					onSubmit={(event) => {
						event.preventDefault()
						if (
							!disabled &&
							targetTeamId &&
							targets.some((team) => team.id === targetTeamId) &&
							!transfer.isPending
						)
							transfer.mutate({ issueId, teamId, targetTeamId, updatedAt })
					}}
				>
					<DialogHeader variant="divided">
						<DialogTitle>Transfer issue</DialogTitle>
						<DialogDescription>
							Move this issue to another team in the same organization.
						</DialogDescription>
					</DialogHeader>
					<div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 py-4 sm:px-6">
						<IssueTransferFields
							teams={targets}
							value={targetTeamId}
							onChange={setTargetTeamId}
							disabled={transfer.isPending || teams.isLoading || teams.isError}
						/>
						{teams.isError && (
							<p role="alert" className="text-sm text-text-sub">
								Unable to load teams. Close this dialog and try again.
							</p>
						)}
						{!teams.isLoading && !teams.isError && targets.length === 0 && (
							<p role="status" className="text-sm text-text-sub">
								No other eligible teams. You need permission to create this type
								of issue there; queued issues also need Queue enabled.
							</p>
						)}
					</div>
					<DialogFooter variant="divided">
						<DialogClose asChild>
							<Button variant="outline" disabled={transfer.isPending}>
								Cancel
							</Button>
						</DialogClose>
						<Button
							type="submit"
							disabled={
								disabled ||
								transfer.isPending ||
								!targetTeamId ||
								!targets.some((team) => team.id === targetTeamId)
							}
						>
							{transfer.isPending ? "Transferring…" : "Transfer issue"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
